import { motion } from 'framer-motion';

const StringMatchVisualization = ({ step, lessonId }) => {
  if (!step) return null;

  const { text = '', pattern = '', textIndex = -1, patternIndex = -1, matchStatus, comparisons = 0, found = [], lps, patternHash, windowHash, algo } = step;

  const getTextCharStyle = (idx) => {
    if (textIndex >= 0 && idx >= textIndex && idx < textIndex + pattern.length) {
      // Char within current window
      const posInPattern = idx - textIndex;
      if (posInPattern === patternIndex) {
        if (matchStatus === 'match' || matchStatus === 'found') return 'bg-green-500/30 border-green-500 text-green-400';
        if (matchStatus === 'mismatch') return 'bg-red-500/30 border-red-500 text-red-400';
        return 'bg-[#FFB800]/30 border-[#FFB800] text-[#FFB800]';
      }
      if (posInPattern < patternIndex) {
        return matchStatus === 'mismatch' ? 'bg-red-500/10 border-red-500/40 text-red-400/80' : 'bg-green-500/15 border-green-500/40 text-green-400/80';
      }
      return 'bg-[#FFB800]/10 border-[#FFB800]/40 text-[#FFB800]/80';
    }
    // Found matches highlighted lightly
    for (const f of found) {
      if (idx >= f && idx < f + pattern.length) {
        return 'bg-green-500/10 border-green-500/30 text-green-400';
      }
    }
    return 'bg-[#1A1A20] border-white/10 text-white';
  };

  const getPatternCharStyle = (idx) => {
    if (idx === patternIndex) {
      if (matchStatus === 'match' || matchStatus === 'found') return 'bg-green-500/30 border-green-500 text-green-400';
      if (matchStatus === 'mismatch') return 'bg-red-500/30 border-red-500 text-red-400';
      return 'bg-[#FFB800]/30 border-[#FFB800] text-[#FFB800]';
    }
    if (idx < patternIndex) return 'bg-green-500/10 border-green-500/40 text-green-400/80';
    return 'bg-[#1A1A20] border-white/10 text-white';
  };

  const cellWidth = 32;

  return (
    <div className="space-y-4">
      {/* Text */}
      <div>
        <div className="text-[#71717A] text-xs font-code mb-2">TEXT</div>
        <div className="flex gap-1 overflow-x-auto pb-2">
          {text.split('').map((ch, idx) => (
            <div key={idx} className="flex flex-col items-center flex-shrink-0">
              <motion.div
                className={`w-8 h-10 flex items-center justify-center border-2 rounded font-code font-bold text-lg ${getTextCharStyle(idx)}`}
                animate={{ scale: 1 }}
              >
                {ch}
              </motion.div>
              <div className="text-[9px] text-[#71717A] font-code mt-1">{idx}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Pattern (shifted to current textIndex) */}
      <div>
        <div className="text-[#71717A] text-xs font-code mb-2">PATTERN</div>
        <div className="flex gap-1 overflow-x-auto pb-2 relative">
          {textIndex >= 0 && (
            <motion.div
              className="flex gap-1"
              animate={{ marginLeft: `${textIndex * (cellWidth + 4)}px` }}
              transition={{ type: 'spring', stiffness: 200, damping: 25 }}
            >
              {pattern.split('').map((ch, idx) => (
                <div key={idx} className="flex flex-col items-center flex-shrink-0">
                  <div className={`w-8 h-10 flex items-center justify-center border-2 rounded font-code font-bold text-lg ${getPatternCharStyle(idx)}`}>
                    {ch}
                  </div>
                  <div className="text-[9px] text-[#71717A] font-code mt-1">{idx}</div>
                </div>
              ))}
            </motion.div>
          )}
          {textIndex < 0 && (
            <div className="flex gap-1">
              {pattern.split('').map((ch, idx) => (
                <div key={idx} className="flex flex-col items-center flex-shrink-0">
                  <div className="w-8 h-10 flex items-center justify-center border-2 rounded font-code font-bold text-lg bg-[#1A1A20] border-white/10 text-white">
                    {ch}
                  </div>
                  <div className="text-[9px] text-[#71717A] font-code mt-1">{idx}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* LPS Array for KMP */}
      {algo === 'kmp' && lps && (
        <div className="bg-[#0F0F12] border border-[#007AFF]/20 rounded p-3">
          <div className="text-[#007AFF] text-xs font-code mb-2">LPS ARRAY (longest proper prefix that is also suffix)</div>
          <div className="flex gap-1 overflow-x-auto">
            {pattern.split('').map((ch, idx) => (
              <div key={idx} className="flex flex-col items-center">
                <div className="w-8 h-8 flex items-center justify-center border border-[#007AFF]/40 bg-[#007AFF]/10 text-[#007AFF] rounded font-code text-xs font-bold">
                  {lps[idx]}
                </div>
                <div className="text-[9px] text-[#71717A] font-code mt-1">{ch}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Rabin-Karp hashes */}
      {algo === 'rabin-karp' && (
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-[#0F0F12] border border-[#FFB800]/20 rounded p-3 text-center">
            <div className="text-[#FFB800] text-xs font-code mb-1">PATTERN HASH</div>
            <div className="text-white text-xl font-code font-bold">{patternHash}</div>
          </div>
          <div className="bg-[#0F0F12] border border-[#007AFF]/20 rounded p-3 text-center">
            <div className="text-[#007AFF] text-xs font-code mb-1">WINDOW HASH</div>
            <div className={`text-xl font-code font-bold ${windowHash === patternHash ? 'text-green-400' : 'text-white'}`}>
              {windowHash === null ? '-' : windowHash}
            </div>
          </div>
        </div>
      )}

      {/* Comparisons + Found */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-[#0F0F12] border border-white/10 rounded p-3 text-center">
          <div className="text-[#71717A] text-xs font-code mb-1">COMPARISONS</div>
          <div className="text-[#FFB800] text-xl font-code font-bold">{comparisons}</div>
        </div>
        <div className="bg-[#0F0F12] border border-green-500/20 rounded p-3 text-center">
          <div className="text-green-400 text-xs font-code mb-1">MATCHES FOUND</div>
          <div className="text-white text-xl font-code font-bold">
            {found.length === 0 ? '0' : `[${found.join(', ')}]`}
          </div>
        </div>
      </div>

      {/* Result */}
      {step.result !== undefined && (
        <div className="bg-gradient-to-r from-green-500/10 to-green-400/10 border border-green-500/30 rounded-lg p-4 text-center">
          <div className="text-[#A1A1AA] text-sm font-body mb-1">Match Indices</div>
          <div className="text-green-400 text-2xl font-code font-bold">{step.result}</div>
        </div>
      )}
    </div>
  );
};

export default StringMatchVisualization;
